/**
 * Shared derived-value engine.
 *
 * Four surfaces render echo measurements — the measurement workspace, the OHIF viewer
 * panel, the viewer worksheet, and the patient-side Echo Measurements table — and each
 * one used to re-derive E/e', RVSP and the diastolic grade for itself. The copies had
 * drifted apart, so the same study produced different numbers depending on which screen
 * was open, and a signed report could disagree with the screen it was approved on:
 *
 *   - RVSP: the workspace passed an IVC-derived right atrial pressure; the other three
 *     passed nothing, so rvsp() silently fell back to 5 mmHg. With a dilated,
 *     non-collapsing IVC (RAP 15) that is a ~15 mmHg understatement — the difference
 *     between calling pulmonary hypertension and not.
 *   - E/e': the workspace averaged septal and lateral e' per ASE; the other three used
 *     septal alone. Septal e' is normally the lower of the two, so a septal-only ratio
 *     reads high and overstates filling pressure — which then feeds diastolic grading.
 *
 * Everything derivable from the primary values now lives here, once. Callers keep only
 * what genuinely differs: the workspace also computes biplane Simpson volumes from
 * annotation geometry and BSA-indexed values, neither of which the read-only surfaces
 * can reach.
 *
 * Pure: no React, no Cornerstone, no DOM. Returns raw numbers; each caller applies its
 * own null-filtering and rounding.
 */
import * as echoCalc from './echoCalc';

/** Reads a primary (measured or typed) value by catalog id. */
export type MeasurementGetter = (id: string) => number | null;

export interface SharedDerivedOptions {
  /**
   * LA volume index, when the caller derived it itself. The workspace computes LAVi from
   * LA area-length and BSA; read-only surfaces only have whatever LAVi was stored. Pass
   * `undefined` (or omit) to fall back to the stored LAVi.
   */
  lavi?: number | null;
  /**
   * True only when the operator TYPED this value, overriding the computed one.
   *
   * Derived rows are persisted alongside measured ones and hydrate back into the same
   * value map, so a bare `v('RVSP')` cannot tell "the clinician entered 55" from "we
   * computed 36 last week and saved it". Without this the diastolic grade would prefer a
   * stale saved RVSP over one recomputed from the current TR jet and IVC — silently
   * grading against last session's pressure.
   *
   * Defaults to "nothing is a typed override", i.e. always trust the fresh computation.
   */
  isTypedOverride?: (id: string) => boolean;
  /**
   * The ASE general diastolic algorithm is only applicable after a clinician confirms
   * sinus rhythm and that no special-population pathway is required.  Omit or pass
   * false to withhold the grade; this never affects the primary measurements or any
   * other derived calculation.
   */
  allowGeneralDiastolicGrade?: boolean;
}

/**
 * Every value derivable from the primaries alone. Null entries mean "not computable from
 * what has been measured" and callers are expected to drop them rather than display them.
 */
export function deriveSharedEchoValues(
  v: MeasurementGetter,
  options: SharedDerivedOptions = {}
): Record<string, number | null> {
  // Right atrial pressure from IVC diameter + collapsibility (ASE tiering). When the IVC
  // was never measured this is null and rvsp() applies the intermediate 8 mmHg.
  const rap = echoCalc.rapFromIVC(v('IVC_diam'), v('IVC_collapse'));
  const rvspValue = echoCalc.rvsp(v('TR_Vmax'), rap);

  // E/e' — ASE averages septal and lateral e' when both annuli were sampled, otherwise
  // uses whichever single one was.
  const eSeptal = v('e_prime_septal');
  const eLateral = v('e_prime_lateral');
  const eAverage =
    eSeptal != null && eLateral != null ? (eSeptal + eLateral) / 2 : (eSeptal ?? eLateral);
  const eOverEprime = echoCalc.ratio(v('MV_E'), eAverage);

  const eaRatio = echoCalc.ratio(v('MV_E'), v('MV_A'));
  const lavi = options.lavi !== undefined ? options.lavi : v('LAVi');

  // A typed value wins; a persisted derived one must not. See isTypedOverride above.
  const typed = options.isTypedOverride ?? (() => false);
  const typedOr = (id: string, computed: number | null): number | null =>
    typed(id) ? (v(id) ?? computed) : computed;

  const derived: Record<string, number | null> = {
    LV_EF: echoCalc.ejectionFraction(v('LV_EDV'), v('LV_ESV')),
    RAP: rap,
    RVSP: rvspValue,
    AV_peakPG: echoCalc.bernoulliGradient(v('AV_Vmax')),
    E_A_ratio: eaRatio,
    E_e_prime: eOverEprime,
    LV_mass: echoCalc.lvMassCube(v('LVIDd'), v('IVSd'), v('LVPWd')),
    mPAP: echoCalc.meanPAP(v('PV_accelTime')),
    LV_dPdt: echoCalc.lvDpDt(v('dPdt_time')),
    Tei: echoCalc.teiIndex(v('IVCT'), v('IVRT'), v('ET')),
    TAPSE_grade: echoCalc.tapseGrade(v('TAPSE')),
  };

  // ASE 2025 diastolic-function grade (Nagueh et al., JASE 2025) — coded 0–3/9; label
  // via catalog valueLabels. The general algorithm must never silently be applied to
  // AF, significant valve disease, pulmonary hypertension, HCM, transplant,
  // constriction or LVAD. The caller supplies an explicit clinician confirmation.
  derived.DIAST_GRADE = options.allowGeneralDiastolicGrade
    ? echoCalc.diastolicGrade({
        eSeptal,
        eLateral,
        eOverEprime,
        trVmax: v('TR_Vmax'),
        pasp: typedOr('RVSP', rvspValue),
        lavi,
        larStrain: v('LA_reservoir_strain'),
        pvSD: v('PV_SD_ratio'),
        ivrt: v('IVRT'),
        eaRatio: typedOr('E_A_ratio', eaRatio),
        mvE: v('MV_E'),
      })
    : null;

  return derived;
}
